import Earth16 from '@carbon/icons-react/es/earth/16';
import List16 from '@carbon/icons-react/es/list/16';
import React, { useMemo } from 'react';
import { StatusFieldOption, StatusGroup } from 'api/tasks';
import { View, ViewType } from 'api/views/types';
import { QueryCombinerType } from 'components/organisms/tasks/combiner';
import { SingleSelectField } from 'components/organisms/tasks/fields';
import { SelectFilterOperationType } from 'components/organisms/tasks/filters/select';
import { defaultGroupedFilter } from 'components/organisms/tasks/utils';
import { GroupFieldsMeta } from 'components/pages/filter-transformer';
import { FilterGroupType } from 'components/pages/types';
import { useTranslation } from 'hooks/use-translation';
import { FieldHelper } from 'utils/field';
import { ResourceManagementViewDefinition } from './types';

export const DEFAULT_VIEW_ID = -1;

const getCompletedOrCancelledStatusIds = (lookup: GroupFieldsMeta) => {
  const projectFieldsLookup = lookup.find(
    (group) => group.id === FilterGroupType.Project,
  );

  const status = projectFieldsLookup?.fields.status as SingleSelectField;

  const completedOrCancelledIds = status
    ? FieldHelper(status)
        .options.activeOptions.filter((x) =>
          [StatusGroup.Completed, StatusGroup.Cancelled].includes(
            (x as StatusFieldOption).group,
          ),
        )
        .map(({ value }) => value)
    : undefined;

  return {
    status,
    completedOrCancelledIds,
  };
};

export const useGroupByUsersDefaultViews = (
  lookup: GroupFieldsMeta,
  isFieldsLoading: boolean,
): View<ResourceManagementViewDefinition>[] => {
  const allUsersActiveProjectsLabel = useTranslation(
    'resource_management.views.all_users_active_projects',
  );

  if (isFieldsLoading) {
    return [];
  }

  const { status, completedOrCancelledIds } =
    getCompletedOrCancelledStatusIds(lookup);

  return [
    {
      viewId: DEFAULT_VIEW_ID,
      viewName: allUsersActiveProjectsLabel,
      viewDefinition: {
        filter: {
          type: QueryCombinerType.All,
          defaultGroup: FilterGroupType.Project,
          groupCriteria: [
            {
              id: FilterGroupType.Project,
              criteria: {
                field: status,
                type: SelectFilterOperationType.IsNoneOf,
                value: completedOrCancelledIds,
              },
            },
          ],
        },
      },
      viewIcon: <List16 />,
      createdBy: -1,
      self: false,
      viewType: ViewType.ResourceManagement,
    },
  ];
};

export const useGroupByProjectsDefaultViews = (
  lookup: GroupFieldsMeta,
  isFieldsLoading: boolean,
): View<ResourceManagementViewDefinition>[] => {
  const activeProjectsLabel = useTranslation(
    'resource_management.views.active_projects',
  );
  const allProjectsLabel = useTranslation(
    'resource_management.views.all_projects',
  );

  const allProjectsView = useMemo(
    () => [
      {
        viewId: DEFAULT_VIEW_ID - 1,
        viewName: allProjectsLabel,
        viewDefinition: {
          filter: defaultGroupedFilter(FilterGroupType.Project),
        },
        createdBy: -1,
        viewIcon: <Earth16 />,
        self: false,
        viewType: ViewType.ResourceManagement,
      },
    ],
    [allProjectsLabel],
  );

  if (isFieldsLoading) {
    return allProjectsView;
  }

  const { status, completedOrCancelledIds } =
    getCompletedOrCancelledStatusIds(lookup);

  return [
    {
      viewId: DEFAULT_VIEW_ID,
      viewName: activeProjectsLabel,
      viewDefinition: {
        filter: {
          type: QueryCombinerType.All,
          defaultGroup: FilterGroupType.Project,
          groupCriteria: [
            {
              id: FilterGroupType.Project,
              criteria: {
                field: status,
                type: SelectFilterOperationType.IsNoneOf,
                value: completedOrCancelledIds,
              },
            },
          ],
        },
      },
      viewIcon: <List16 />,
      createdBy: -1,
      self: false,
      viewType: ViewType.ResourceManagement,
    },
    ...allProjectsView,
  ];
};
